<script setup lang="ts">
import FeaturedIcon from '@/common/components/molecules/featuredIcon/FeaturedIcon.vue';

import { useSlots } from 'vue';
import { setup, type Props, type Emits } from './menuSearchReulstItem.setup';

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const slots = useSlots();

const { isSelected, highlightSegments, handleClick } = setup(props, emit);
</script>

<template>
  <li
    :class="['menu-search-result-item', { selected: isSelected }]"
    :data-menu-search-item-id="props.id"
    @click="handleClick"
  >
    <div class="menu-search-result-item__content">
      <featured-icon
        v-if="props.featuredIcon"
        :icon-code="props.featuredIcon"
        size="small"
        :show-tooltip="false"
      />
      <i
        v-else
        :class="['menu-search-result-item__icon', `menu-search-result-item__${props.icon}`]"
      />
      <p class="menu-search-result-item__title">
        <span
          v-for="(segment, index) in highlightSegments"
          :key="`${segment.text}_${index}`"
          :class="[
            'menu-search-result-item__title-text',
            { 'menu-search-result-item__title-text--highlight': segment.match },
          ]"
        >
          {{ segment.text }}
        </span>
      </p>
      <span
        v-if="slots.subTitle"
        class="menu-search-result-item__sub-title"
      >
        <slot name="subTitle" />
      </span>
    </div>
    <slot name="suffix" />
  </li>
</template>

<style scoped lang="scss">
.menu-search-result-item {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  padding: 8px;
  cursor: pointer;

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    pointer-events: none;
    border-radius: 2px;
    content: '';
  }

  &:hover::before {
    opacity: 0.06;
    background: var(--color-gray-00-12);
  }

  &.selected::before {
    opacity: 0.1;
    background: var(--color-blue-05-05);
  }

  &__content {
    display: flex;
    position: relative;
    z-index: 1;
    gap: 12px;
    align-items: center;
  }

  &__icon {
    display: inline-block;
    width: 16px;
    height: 16px;
    background-color: var(--color-gray-03-09);
    mask-position: center center;
    mask-size: 16px 16px;
    mask-repeat: no-repeat;

    &--home {
      mask-image: url('@/common/assets/gnb/icon-home.svg');
    }

    &--dashboard {
      mask-image: url('@/common/assets/gnb/icon-dashboard.svg');
    }

    &--aiops {
      mask-image: url('@/common/assets/gnb/icon-aiops.svg');
    }

    &--database {
      mask-image: url('@/common/assets/gnb/icon-database.svg');
    }

    &--application {
      mask-image: url('@/common/assets/gnb/icon-application.svg');
    }

    &--infrastructure {
      mask-image: url('@/common/assets/gnb/icon-infrastructure.svg');
    }

    &--cloud {
      mask-image: url('@/common/assets/gnb/icon-cloud.svg');
    }

    &--message-queue {
      mask-image: url('@/common/assets/gnb/icon-message-queue.svg');
    }

    &--kubernetes {
      mask-image: url('@/common/assets/gnb/icon-kubernetes.svg');
    }

    &--gpu {
      mask-image: url('@/common/assets/gnb/icon-gpu.svg');
    }

    // &--pa {
    //   mask-image: url('@/common/assets/gnb/icon-pa.svg');
    // }

    &--report {
      mask-image: url('@/common/assets/gnb/icon-report.svg');
    }

    &--predefined-report {
      mask-image: url('@/common/assets/gnb/icon-predefined-report.svg');
    }

    &--alert {
      mask-image: url('@/common/assets/gnb/icon-alert.svg');
    }

    &--business {
      mask-image: url('@/common/assets/gnb/icon-business.svg');
    }

    &--logs {
      mask-image: url('@/common/assets/gnb/icon-logs.svg');
    }

    &--oracle {
      mask-image: url('@/common/assets/icon-nav-oracle.svg');
    }

    &--postgresql {
      mask-image: url('@/common/assets/icon-nav-postgresql.svg');
    }

    &--mysql {
      mask-image: url('@/common/assets/icon-nav-mysql.svg');
    }

    &--sqlserver {
      mask-image: url('@/common/assets/icon-nav-sqlserver.svg');
    }

    &--redis {
      mask-image: url('@/common/assets/icon-nav-redis.svg');
    }

    &--mongodb {
      mask-image: url('@/common/assets/icon-nav-mongodb.svg');
    }

    &--tibero {
      mask-image: url('@/common/assets/icon-nav-tibero.svg');
    }

    &--altibase {
      mask-image: url('@/common/assets/icon-nav-altibase.svg');
    }

    &--redshift {
      mask-image: url('@/common/assets/icon-nav-redshift.svg');
    }

    &--cubrid {
      mask-image: url('@/common/assets/icon-nav-cubrid.svg');
    }

    &--clickhouse {
      mask-image: url('@/common/assets/icon-nav-clickhouse.svg');
    }

    &--singlestore {
      mask-image: url('@/common/assets/icon-nav-singlestore.svg');
    }

    &--tool {
      mask-image: url('@/common/assets/gnb/icon-tool.svg');
    }
  }

  &__title {
    display: block;
    color: var(--color-gray-01-11);
    white-space: pre-wrap;

    @include typography('body-2');

    &-text {
      display: inline;
    }

    &-text--highlight {
      font-weight: 700;
      color: var(--color-blue-05-05);
    }
  }

  &__sub-title {
    display: flex;
    align-items: center;
    line-height: 120%;

    @include typography('body-3');
  }
}
</style>

<style lang="scss">
.menu-search-result-item {
  &__sub-title {
    .menu-sub-title {
      color: var(--color-gray-04-08);
    }

    .dashboard-sub-title {
      display: flex;
      gap: 4px;
      align-items: center;
      justify-content: center;
      height: 20px;
      padding: 4px 6px;
      border-radius: 4px;

      &--custom {
        background: var(--tag-tibero-bg-color);
        color: var(--tag-tibero-font-color);
      }

      &--preset {
        background: var(--color-gray-10-02);
        color: var(--color-gray-02-10);
      }
    }
  }

  &__suffix {
    display: flex;
    position: relative;
    z-index: 1;
    gap: 12px;
    align-items: center;

    &.ev-button.type-icon {
      &:hover {
        background-color: transparent !important;
      }

      .icon-fill-star {
        background-color: var(--color-yellow-05-05) !important;
      }
    }
  }
}
</style>
